import { Button, ButtonVariants } from '@components/button/Button';
import { Modal } from '@components/modal/Modal';
import { isAddOnAvailableForTier } from '@shared/restaurantEvents/addOnAvailability';
import type { RestaurantEventAddOn } from '@shared/types/restaurantEvents';
import { centsToWholeDollar } from '@shared/utils/currency';
import { useState } from 'react';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import { AddOnCard } from './AddOnCard';
import type { SelectedAddOn } from './apiHelpers';
import styles from './AddOnSelectionModal.scss';

interface AddOnSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  addOns: RestaurantEventAddOn[];
  onContinue: (selectedAddOns: SelectedAddOn[]) => void;
  onSkip: () => void;
  selectedTierId: string;
}

export const AddOnSelectionModal = ({
  isOpen,
  onClose,
  addOns,
  onContinue,
  onSkip,
  selectedTierId,
}: AddOnSelectionModalProps) => {
  const [selections, setSelections] = useState<Map<string, number>>(new Map());

  const availableAddOns = addOns.filter((addOn) =>
    isAddOnAvailableForTier(addOn, selectedTierId),
  );

  const handleIncrement = (addOnId: string) => {
    setSelections((prev) => {
      const newSelections = new Map(prev);
      const currentQuantity = newSelections.get(addOnId) ?? 0;
      newSelections.set(addOnId, currentQuantity + 1);
      return newSelections;
    });
  };

  const handleDecrement = (addOnId: string) => {
    setSelections((prev) => {
      const newSelections = new Map(prev);
      const currentQuantity = newSelections.get(addOnId) ?? 0;
      if (currentQuantity <= 1) {
        newSelections.delete(addOnId);
      } else {
        newSelections.set(addOnId, currentQuantity - 1);
      }
      return newSelections;
    });
  };

  const handleContinue = () => {
    const selectedAddOns: SelectedAddOn[] = Array.from(
      selections.entries(),
    ).map(([addOnId, quantity]) => ({ addOnId, quantity }));
    onContinue(selectedAddOns);
  };

  const handleClose = () => {
    setSelections(new Map());
    onClose();
  };

  const handleSkip = () => {
    setSelections(new Map());
    onSkip();
  };

  const totalSelectedCount = Array.from(selections.values()).reduce(
    (sum, qty) => sum + qty,
    0,
  );

  const totalPrice = Array.from(selections.entries()).reduce(
    (sum, [addOnId, quantity]) => {
      const addOn = addOns.find((a) => a.id === addOnId);
      return sum + (addOn?.price ?? 0) * quantity;
    },
    0,
  );

  const hasSelections = totalSelectedCount > 0;

  return (
    <Modal
      ariaLabel="Add-On Selection Modal"
      isOpen={isOpen}
      onClose={handleClose}
    >
      <div className={styles.content}>
        <h2 className={cx(typography.h4)}>Purchase Add-Ons</h2>
        <div className={styles.grid}>
          {availableAddOns.map((addOn) => (
            <AddOnCard
              key={addOn.id}
              addOn={addOn}
              quantity={selections.get(addOn.id) ?? 0}
              onIncrement={() => handleIncrement(addOn.id)}
              onDecrement={() => handleDecrement(addOn.id)}
            />
          ))}
        </div>
        <div className={styles.actions}>
          {hasSelections ? (
            <Button
              variant={ButtonVariants.Primary}
              label={`Purchase ${totalSelectedCount} Add-On${totalSelectedCount > 1 ? 's' : ''} \u2022 ${centsToWholeDollar(totalPrice)}`}
              onClick={handleContinue}
              className={styles.purchaseButton}
            />
          ) : (
            <Button
              variant={ButtonVariants.Tertiary}
              label="Skip Add-Ons"
              onClick={handleSkip}
              className={styles.skipButton}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
